import React from "react";

/**
 * NumberInput — drop-in <input type="number"> wrapper.
 *
 * Behavior:
 *  - When the bound value is 0 the input renders "0" (NOT empty), so that
 *    HTML5 `required` validation on the underlying <input> passes and forms
 *    that legitimately allow 0 (commissions, initial balance, overrides,
 *    override %, etc.) can be submitted.
 *  - On focus the entire field contents are selected so typing immediately
 *    replaces whatever was there (fixes the "0200" leading-zero UX bug
 *    without hiding legitimate zero values).
 *  - Local text buffer keeps the field editable — the user can clear it,
 *    type freely, and the buffer resyncs from the parent when the input
 *    loses focus.
 *  - onChange receives the parsed number as the first arg; the raw string
 *    is passed as the second arg. Pass `asString` to swap the semantics.
 *
 * Usage:
 *   <NumberInput value={amount} onChange={setAmount} placeholder="0" />
 *   <NumberInput value={form.balance}
 *                onChange={(num, raw) => setForm({ ...form, balance: num })} />
 */
export default function NumberInput({
  value,
  onChange,
  step = "0.01",
  min,
  max,
  placeholder = "0",
  className = "",
  disabled,
  required,
  asString = false,
  testId,
  "data-testid": dataTestId,
  onFocus: extraOnFocus,
  onBlur: extraOnBlur,
  ...rest
}) {
  const toText = (v) =>
    v === null || v === undefined || v === "" ? "" : String(v);

  const [text, setText] = React.useState(() => toText(value));
  const [focused, setFocused] = React.useState(false);

  // Sync from parent when the parent's value changes AND the input is not
  // currently being edited — this avoids stomping mid-typing while still
  // reflecting external updates (e.g. presets, resets, async loads).
  React.useEffect(() => {
    if (focused) return;
    const incoming = toText(value);
    setText((prev) => (prev === incoming ? prev : incoming));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, focused]);

  const handleFocus = (e) => {
    setFocused(true);
    // select-all so the next keystroke replaces "0" instead of prefixing it
    e.target.select();
    if (extraOnFocus) extraOnFocus(e);
  };

  const handleBlur = (e) => {
    setFocused(false);
    // If the user cleared the field, resync display to the numeric value so
    // the DOM value is never "" on submit (required validation would fail).
    if (text === "") setText(toText(value));
    if (extraOnBlur) extraOnBlur(e);
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    setText(raw);
    if (asString) {
      onChange(raw, raw === "" ? "" : parseFloat(raw));
    } else {
      const parsed = raw === "" ? 0 : parseFloat(raw);
      onChange(isNaN(parsed) ? 0 : parsed, raw);
    }
  };

  return (
    <input
      type="number"
      step={step}
      min={min}
      max={max}
      value={text}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      required={required}
      className={className}
      data-testid={dataTestId || testId}
      {...rest}
    />
  );
}
